import classNames from 'classnames'
import { components } from 'react-select'

import type { ComponentType, MouseEvent, ReactElement, ReactNode } from 'react'
import type { ControlProps, GroupBase, MenuProps, OptionProps, ValueContainerProps } from 'react-select'
import type { IOptionWithIcon } from '~/types/option'

import Button from '~/components/Buttons/Button'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'

export const Menu = (props: MenuProps, classes: string): ReactElement => {
  return (
    <components.Menu {...props} className={classes}>
      {props.children}
    </components.Menu>
  )
}

export const Option = (
  props: OptionProps,
  CustomOption?: ComponentType<OptionProps<unknown, boolean, GroupBase<unknown>>>,
  onOnlyButtonClick?: (value: IOptionWithIcon) => void
): ReactElement => {
  /** Local state */

  const { className, data, isSelected, label } = props

  const { icon, value } = data as IOptionWithIcon

  /** Handlers */

  const handleOnlyButtonClick = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    onOnlyButtonClick && onOnlyButtonClick(data as IOptionWithIcon)
  }

  /** Render */

  if (value !== 'all') {
    return (
      <components.Option {...props} className={classNames(className, { 'with-only-btn': !!onOnlyButtonClick })}>
        <CheckboxInput checked={isSelected} className="react-select__option__checkbox" readOnly size="small" />
        {icon ? <div className="option-icon-wrapper">{icon}</div> : null}
        {CustomOption ? (
          <CustomOption {...props} />
        ) : (
          <div className="option-label" title={label}>
            {label}
          </div>
        )}
        {onOnlyButtonClick ? (
          <Button className="only-button" onClick={handleOnlyButtonClick} size="small">
            Only
          </Button>
        ) : null}
      </components.Option>
    )
  }

  return (
    <components.Option {...props} className="select-all-option">
      <Button ghost size="small">
        {label}
      </Button>
    </components.Option>
  )
}

export const Control = (props: ControlProps): ReactElement => {
  const { children, getValue, hasValue, selectProps } = props

  const castedGetValue = getValue() as IOptionWithIcon[]

  const optionIcon = castedGetValue.length > 0 && castedGetValue[0].icon

  const { defaultIcon } = selectProps

  return (
    <components.Control {...props}>
      {hasValue && defaultIcon ? (
        <>
          {getValue().length < 2 && optionIcon ? (
            <div className="icon-wrapper">{optionIcon}</div>
          ) : (
            <div className="icon-wrapper">{defaultIcon}</div>
          )}
        </>
      ) : null}
      {children}
    </components.Control>
  )
}

export const ValueContainer = (props: ValueContainerProps): ReactElement => {
  const { children, getValue, hasValue, selectProps } = props

  const [, input] = children as ReactNode[]

  const valuesCount = getValue().length

  const { menuIsOpen, optionType } = selectProps

  return (
    <components.ValueContainer {...props}>
      {!menuIsOpen && hasValue && valuesCount > 1 ? (
        <>
          <span>
            {valuesCount} {optionType ?? 'Options'}
          </span>
          {input}
        </>
      ) : (
        children
      )}
    </components.ValueContainer>
  )
}
